<script setup>
import { nextTick } from 'vue';

// Tabs estilo underline, endereçadas por CHAVE (não por índice): o pai declara
// `{ key, label, icon?, count?, disabled?, disabledHint?, dot?, dotLabel? }` e
// controla via v-model — trocar a ordem dos itens não quebra quem está ativo.
//
// `dot` ('ruby' | 'amber') marca a aba com algo pendente do lado de dentro
// (erro, alteração não salva) sem abrir a aba; `dotLabel` diz o quê pro leitor
// de tela, já que a cor sozinha não diz nada a ele.
//
// Aba desabilitada usa `aria-disabled` + guarda no clique, NÃO o atributo
// `disabled`: elemento desabilitado não recebe evento de ponteiro, e aí o
// tooltip que explica POR QUE ela está travada nunca aparece — some justo a
// frase que diz como destravar. Alternativa ao TabBar (pílulas segmentadas,
// index-based) para telas onde as abas dividem SEÇÕES de conteúdo.
const props = defineProps({
  tabs: {
    type: Array,
    required: true,
    validator: value =>
      value.every(
        tab => typeof tab.key === 'string' && typeof tab.label === 'string'
      ),
  },
  modelValue: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const buttonRefs = [];
const setButtonRef = (el, index) => {
  buttonRefs[index] = el;
};

const isDisabled = tab => Boolean(tab.disabled);

const select = tab => {
  if (isDisabled(tab)) return;
  emit('update:modelValue', tab.key);
};

// Navegação padrão de tablist (WAI-ARIA): setas movem seleção + foco juntos.
const onKeydown = event => {
  const keys = props.tabs.map(tab => tab.key);
  const current = keys.indexOf(props.modelValue);
  let next = null;
  if (event.key === 'ArrowRight') next = (current + 1) % keys.length;
  else if (event.key === 'ArrowLeft')
    next = (current - 1 + keys.length) % keys.length;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = keys.length - 1;
  if (next === null) return;

  // Pula as travadas: parar o foco numa aba que não abre é beco sem saída.
  const step = event.key === 'ArrowLeft' ? -1 : 1;
  let guard = keys.length;
  while (isDisabled(props.tabs[next]) && guard > 0) {
    next = (next + step + keys.length) % keys.length;
    guard -= 1;
  }
  if (isDisabled(props.tabs[next])) return;

  event.preventDefault();
  select(props.tabs[next]);
  nextTick(() => buttonRefs[next]?.focus());
};

const showCount = tab => Number.isFinite(tab.count);
</script>

<template>
  <div
    role="tablist"
    class="flex items-center gap-1 overflow-x-auto border-b border-n-weak [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    @keydown="onKeydown"
  >
    <button
      v-for="(tab, index) in tabs"
      :key="tab.key"
      :ref="el => setButtonRef(el, index)"
      v-tooltip.bottom="tab.disabled ? tab.disabledHint : ''"
      type="button"
      role="tab"
      :aria-selected="modelValue === tab.key"
      :aria-disabled="isDisabled(tab)"
      :tabindex="modelValue === tab.key ? 0 : -1"
      class="-mb-px flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-t-md rounded-b-none border-b-2 px-3 py-2 text-sm font-medium outline-none transition-colors focus-visible:bg-n-alpha-1"
      :class="[
        modelValue === tab.key
          ? 'border-n-brand-ink text-n-slate-12'
          : 'border-transparent text-n-slate-11',
        isDisabled(tab)
          ? 'cursor-not-allowed text-n-slate-10'
          : 'hover:border-n-slate-6 hover:text-n-slate-12',
      ]"
      @click="select(tab)"
    >
      <span
        v-if="tab.icon"
        class="size-4 flex-shrink-0"
        :class="[
          tab.icon,
          modelValue === tab.key && !isDisabled(tab) ? 'text-n-brand-ink' : '',
        ]"
      />
      {{ tab.label }}
      <span
        v-if="isDisabled(tab)"
        class="i-lucide-lock size-3 flex-shrink-0 text-n-slate-10"
      />
      <span
        v-if="tab.dot"
        class="size-1.5 flex-shrink-0 rounded-full"
        :class="tab.dot === 'ruby' ? 'bg-n-ruby-9' : 'bg-n-amber-9'"
        :data-test="`tab-dot-${tab.key}`"
      />
      <span v-if="tab.dot && tab.dotLabel" class="sr-only">
        {{ tab.dotLabel }}
      </span>
      <span
        v-if="showCount(tab)"
        class="rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums"
        :class="
          modelValue === tab.key
            ? 'bg-n-blue-3 text-n-blue-11'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
      >
        {{ tab.count }}
      </span>
    </button>
  </div>
</template>
